import { Component, model, input, output, OnChanges, SimpleChanges } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IntegrationConfig, IntegrationConfiguration } from '@/core/models/IntegrationEntity';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { Password } from 'primeng/password';
import { firstValueFrom } from 'rxjs';
import { IntegrationService } from '@/core/services/open-api/integration.service';
import { ToastService } from '@/core/services/toast.service';
import { IntegrationHelper } from '../../integration-services/integration-helper';
import { IntegrationCardData } from '@/home/settings/integrations/integration-card/integration-card.types';
import { AppCommonModule } from '@/common/app.common.module';

@Component({
    selector: 'app-integration-api-key-dialog',
    standalone: true,
    imports: [
        TooltipModule,
        TranslatePipe,
        DialogModule,
        FormsModule,
        InputTextModule,
        ButtonModule,
        Password,
        AppCommonModule,
    ],
    templateUrl: './integration-api-key-dialog.component.html',
    // styleUrls: ['./integration-api-key-dialog.component.scss'],
})
export class IntegrationApiKeyDialogComponent implements OnChanges {
    integration = input.required<IntegrationCardData>();
    showAddApiKeyDialog = model.required<boolean>();
    i18nPrefix = '';
    apiKeyChange = output<string>();
    apiKey: string = '';
    apiKeyPageUrl?: string;
    withEmail = false;
    email: string = '';
    saveDisabled: boolean = true;

    public constructor(
        private readonly translate: TranslateService,
        private readonly integrationService: IntegrationService,
        private readonly toastService: ToastService,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        // reset form on opening the dialog:
        if (changes['showAddApiKeyDialog'] && changes['showAddApiKeyDialog'].currentValue === true) {
            this.apiKey = '';
            this.email = '';
            this.saveDisabled = true;
        }
        if (changes['integration']) {
            this.handleIntegrationChange(changes['integration'].currentValue);
        }
    }

    private handleIntegrationChange(int: IntegrationCardData): void {
        this.apiKeyPageUrl = int.extras?.apiKeyPageUrl ?? undefined;
        this.i18nPrefix = IntegrationHelper.getI18nPrefix(int);
        const authMethod = int.integration?.properties?.authorizationMethod;
        if (authMethod !== 'apiKey' && authMethod !== 'apiKeyWithEmail') {
            this.toastService.toast('UNEXPECTED_ERROR');
            const errorMsg =
                `IntegrationApiKeyDialogComponent - Unexpected authorization method "${authMethod}".` +
                'The component was probably loaded in the wrong context, e.g. an integration using oauth.';
            console.error(errorMsg);
        }
        this.withEmail = authMethod === 'apiKeyWithEmail';
        if (!this.withEmail) {
            this.email = '';
        }
    }

    public async saveApiKey(): Promise<void> {
        const integration = this.integration().integration;
        if (!integration) {
            console.error('IntegrationApiKeyDialogComponent.saveApiKey(): IntegrationConfig is missing.');
            return;
        }
        const conf: IntegrationConfiguration = { apiKey: this.apiKey };
        if (this.withEmail) {
            conf.email = this.email;
        }
        const integrationConfig: IntegrationConfig = {
            service: integration.service,
            configuration: conf,
        };
        try {
            // Re-auth (integration already active): patch so existing configuration (e.g. unitMapping) is preserved.
            const save =
                integration.status === 'active'
                    ? this.integrationService.patchConfig(integration.service, integrationConfig)
                    : this.integrationService.create(integrationConfig);
            await firstValueFrom(save);
            this.showAddApiKeyDialog.set(false);
            IntegrationHelper.showConnectSuccessToast(this.toastService, this.translate, this.integration().name);
            this.apiKeyChange.emit(this.apiKey);
        } catch (error) {
            IntegrationHelper.showConnectErrorToast(this.toastService, this.translate, this.integration().name);
            console.error(`Error saving integration configuration`, error);
        }
    }

    public closeDialog(): void {
        this.showAddApiKeyDialog.set(false);
    }

    public validateInputs(): void {
        let validationSuccess = this.apiKey.trim() !== '';
        if (this.withEmail) {
            // TODO proper email validation
            validationSuccess = validationSuccess && this.email.trim() !== '';
        }
        this.saveDisabled = !validationSuccess;
    }
}
